/**
 * Squeeze (TTM-style) + Squeeze Release — extracted from the `calcDots` squeeze
 * lane in `lib/indicators/customIndexEtf.ts` so the chart Dots lane and these
 * shared defs compute identically (parity-verified).
 *
 *   Bollinger(close, length, bbMult)  inside  Keltner(SMA(close,length) ± kcMult·ATR(length))
 *     squeeze ON  : bbLo > kcLo  AND  bbUp < kcUp  AND  bbUp·bbLo > 0.5
 *     squeeze OFF : bbLo < kcLo  AND  bbUp > kcUp  AND  bbUp·bbLo > 0.5
 *   Release window: 3 bars of "green" after an OFF that follows an ON bar
 *     (the `countDot6` / `wasSqueezing` latch in the Pine source).
 *
 * Squeeze and release are mutually exclusive. A bar is compressing (BB inside
 * KC) or it is not. The original TradingView script can paint both because it
 * uses two plots at the same y — that is a drawing accident, not a state.
 * If compression returns during the countdown, the leftover window is
 * cancelled: the live state is squeeze, and a new release needs a new breakout.
 *
 * Defaults (length 20, bbMult 2, kcMult 1.5) reproduce the Dots squeeze lane
 * exactly; the chart can re-parameterize.
 */
import { familyGroup } from "../indicatorGroups";
import type { OHLCV, IndicatorParams, SharedIndicatorDef } from "../types";
import { smaFull, stdevFull, atrFull } from "./ta";

const len = (v: number | undefined, d: number) => Math.max(1, Math.round(typeof v === "number" && Number.isFinite(v) ? v : d));
const numOr = (v: number | undefined, d: number) => (typeof v === "number" && Number.isFinite(v) ? v : d);

/** Per-bar squeeze on/off + release-window flags. Mirrors the Dots loop. */
export function computeSqueeze(
  data: OHLCV,
  params: IndicatorParams = {},
): { onState: (number | null)[]; release: (number | null)[] } {
  const { closes, highs, lows } = data;
  const n = closes.length;
  const onState: (number | null)[] = new Array(n).fill(0);
  const release: (number | null)[] = new Array(n).fill(0);
  if (n === 0 || !highs || !lows) return { onState, release };

  const length = len(params.length, 20);
  const bbMult = numOr(params.bbMult, 2);
  const kcMult = numOr(params.kcMult, 1.5);
  const releaseBars = len(params.releaseBars, 3); // how long the release window persists

  const avg = smaFull(closes, length); // BB basis = KC basis (Pine uses same SMA)
  const std = stdevFull(closes, length);
  const atr = atrFull(highs, lows, closes, length);

  let wasSqueezing = false;
  let countRel = 0;
  for (let i = 0; i < n; i++) {
    const bbUp = avg[i]! + bbMult * std[i]!;
    const bbLo = avg[i]! - bbMult * std[i]!;
    const shift = kcMult * atr[i]!;
    const kcUp = avg[i]! + shift;
    const kcLo = avg[i]! - shift;
    const sqzOn = bbLo > kcLo && bbUp < kcUp && bbUp * bbLo > 0.5;
    const sqzOff = bbLo < kcLo && bbUp > kcUp && bbUp * bbLo > 0.5;
    if (sqzOn) countRel = 0;
    else if (sqzOff && wasSqueezing) countRel = releaseBars;
    else if (countRel > 0) countRel -= 1;
    wasSqueezing = sqzOn;
    onState[i] = sqzOn ? 1 : 0;
    release[i] = !sqzOn && countRel > 0 ? 1 : 0;
  }
  return { onState, release };
}

const SQUEEZE_PARAMS = [
  { id: "length", label: "Length", kind: "number" as const, default: 20, min: 2, max: 200, step: 1 },
  { id: "bbMult", label: "BB stdev mult", kind: "number" as const, default: 2, min: 0.5, max: 5, step: 0.5 },
  { id: "kcMult", label: "KC ATR mult", kind: "number" as const, default: 1.5, min: 0.5, max: 5, step: 0.5 },
];

const RELEASE_PARAMS = [
  ...SQUEEZE_PARAMS,
  { id: "releaseBars", label: "Release bars", kind: "number" as const, default: 3, min: 1, max: 20, step: 1 },
];

/** Squeeze Signal — Squeeze (compression, yellow) while the TTM squeeze is ON,
 *  else —. Configurable squeeze settings (length / BB / KC). Default reads the
 *  precomputed `sq_on`. */
export const SQUEEZE_SIGNAL: SharedIndicatorDef = {
  id: "mpp-squeeze",
  label: "Afiat - Squeeze + Release",
  group: familyGroup("squeeze"),
  kind: "state",
  format: "state",
  stateLabels: { pos: "Squeeze", neg: "", zero: "—" },
  cellColors: { pos: "#FACC15" }, // yellow squeeze
  panelKey: "sq_on",
  params: SQUEEZE_PARAMS,
  computeSeries(data, params) {
    return computeSqueeze(data, params).onState;
  },
};

/** Release Signal — Release (blue) during the post-release window (the
 *  `releaseBars` "green" bars after the squeeze turns off, only while the
 *  squeeze is actually off). A bar that compresses again is squeeze, not
 *  both. Configurable squeeze settings + how many bars the release persists
 *  (default 3). Default reads the precomputed `sq_rel`. */
export const RELEASE_SIGNAL: SharedIndicatorDef = {
  id: "mpp-release",
  label: "Afiat - Squeeze + Release",
  group: familyGroup("squeeze"),
  kind: "state",
  format: "state",
  stateLabels: { pos: "Release", neg: "", zero: "—" },
  cellColors: { pos: "#3B82F6" }, // blue release
  panelKey: "sq_rel",
  params: RELEASE_PARAMS,
  computeSeries(data, params) {
    return computeSqueeze(data, params).release;
  },
};

/** Squeeze Age — bars the CURRENT squeeze has been continuously ON (0 when not
 *  squeezing). A consecutive on-run, so the latest value always reflects the
 *  current state (no stale carry-over). Default reads the precomputed `sq_age`. */
export const SQUEEZE_AGE: SharedIndicatorDef = {
  id: "mpp-squeeze-age",
  label: "Afiat - Squeeze + Release Age",
  group: familyGroup("squeeze"),
  kind: "age",
  format: "age",
  panelKey: "sq_age",
  params: SQUEEZE_PARAMS,
  computeSeries(data, params) {
    const { onState } = computeSqueeze(data, params);
    const out = new Array<number | null>(onState.length).fill(0);
    let run = 0;
    for (let i = 0; i < onState.length; i++) {
      run = onState[i] === 1 ? run + 1 : 0;
      out[i] = run;
    }
    return out;
  },
};

/** Release Age — bars the CURRENT release window has been continuously ON
 *  (0 when not in a release). A consecutive run, symmetric to Squeeze Age, so
 *  the latest value reflects how many bars into the release we are (1…release
 *  bars). Default reads the precomputed `rel_age`. */
export const RELEASE_AGE: SharedIndicatorDef = {
  id: "mpp-release-age",
  label: "Afiat - Squeeze + Release Age",
  group: familyGroup("squeeze"),
  kind: "age",
  format: "age",
  panelKey: "rel_age",
  params: RELEASE_PARAMS,
  computeSeries(data, params) {
    const { release } = computeSqueeze(data, params);
    const out = new Array<number | null>(release.length).fill(0);
    let run = 0;
    for (let i = 0; i < release.length; i++) {
      run = release[i] === 1 ? run + 1 : 0;
      out[i] = run;
    }
    return out;
  },
};
